import { useEffect, useRef, useState, useCallback } from 'react';

import { getNewEvents, isActiveDeliveryOrder, showDeliveryToast, statusKey } from '../utils/deliveryStatus';

const POLL_INTERVAL_MS = 10000;

// Notificaciones in-app del delivery para el cliente web: sondeo de las ordenes
// mientras haya alguna activa (pausado con la pestana oculta), diff del estado
// contra el mapa persistido y toast por cada transicion alcanzada.
const useDeliveryNotifications = ({ orders, phone, tenantId, onRefreshOrders }) => {
  const [hasUnseenUpdate, setHasUnseenUpdate] = useState(false);
  const seenRef = useRef(null); // mapa orderId -> statusKey
  const storageKey = phone && tenantId ? `delivery_notified_${tenantId}_${phone}` : null;

  const loadSeen = useCallback(() => {
    if (!storageKey) return {};
    try {
      return JSON.parse(localStorage.getItem(storageKey)) || {};
    } catch {
      return {}; // localStorage corrupto: re-sembrar en silencio
    }
  }, [storageKey]);

  // Diff -> toasts: se ejecuta con cada lista fresca de ordenes
  useEffect(() => {
    if (!storageKey) return;
    if (!seenRef.current) seenRef.current = loadSeen();
    const seen = seenRef.current;
    let changed = false;
    const presentIds = new Set();

    orders.forEach((order) => {
      presentIds.add(order.id);
      const key = statusKey(order);
      const prev = seen[order.id];
      if (prev === undefined) {
        // Primera vez que se ve esta orden: sembrar sin notificar (evita
        // spamear el historial completo al entrar a la app)
        seen[order.id] = key;
        changed = true;
        return;
      }
      if (prev === key) return;
      const event = getNewEvents(order, prev);
      seen[order.id] = key;
      changed = true;
      if (event) {
        showDeliveryToast(event, order);
        setHasUnseenUpdate(true);
      }
    });

    // Podar solo con lista exitosa y no vacia: loadMyOrders setea [] cuando el
    // fetch falla y no debemos perder el historial visto por un error de red
    if (orders.length > 0) {
      Object.keys(seen).forEach((id) => {
        if (!presentIds.has(id)) {
          delete seen[id];
          changed = true;
        }
      });
    }

    if (changed) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(seen));
      } catch { /* almacenamiento lleno o bloqueado: seguir sin persistir */ }
    }
  }, [orders, storageKey, loadSeen]);

  // Sondeo: solo con ordenes delivery activas y pestana visible
  useEffect(() => {
    if (!storageKey || !onRefreshOrders) return undefined;
    let intervalId = null;

    const start = () => {
      if (intervalId === null && !document.hidden && orders.some(isActiveDeliveryOrder)) {
        intervalId = setInterval(onRefreshOrders, POLL_INTERVAL_MS);
      }
    };
    const stop = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    const handleVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        onRefreshOrders(); // catch-up inmediato al volver a la pestana
        start();
      }
    };

    stop();
    start();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [orders, storageKey, onRefreshOrders]);

  const clearUnseen = useCallback(() => setHasUnseenUpdate(false), []);

  return { hasUnseenUpdate, clearUnseen };
};

export default useDeliveryNotifications;
